/**
 * ============================================================
 * Geometry Engine
 * MiningSurveyWebGIS
 * ============================================================
 */

import L from "leaflet";

class Geometry {
    /**
     * =========================================================
     * Factory
     * =========================================================
     */

    create(placemark, style = {}) {
        if (!placemark.geometry) {
            return null;
        }

        switch (placemark.geometry.type) {
            case "Point":
                return this.createPoint(placemark.geometry, style);

            case "LineString":
                return this.createLine(placemark.geometry, style);

            case "Polygon":
                return this.createPolygon(placemark.geometry, style);

            default:
                return null;
        }
    }

    /**
     * =========================================================
     * Point
     * =========================================================
     */

    createPoint(geometry, style) {
        const c = geometry.coordinates[0];

        const marker = L.marker([c.lat, c.lng], {
            interactive: true,
        });

        return marker;
    }

    /**
     * =========================================================
     * Polyline
     * =========================================================
     */

    createLine(geometry, style) {
        const latlngs = geometry.coordinates.map((c) => [c.lat, c.lng]);

        return L.polyline(latlngs, this.lineStyle(style));
    }

    /**
     * =========================================================
     * Polygon
     * =========================================================
     */

    createPolygon(geometry, style) {
        const latlngs = geometry.coordinates.map((c) => [c.lat, c.lng]);

        return L.polygon(latlngs, this.polygonStyle(style));
    }

    /**
     * =========================================================
     * Line Style
     * =========================================================
     */

    lineStyle(style) {
        if (!style?.line) {
            return {
                color: "#3388ff",

                weight: 2,

                opacity: 1,
            };
        }

        return {
            color: style.line.color.color,

            opacity: style.line.color.opacity,

            weight: style.line.width,

            lineCap: "round",

            lineJoin: "round",
        };
    }

    /**
     * =========================================================
     * Polygon Style
     * =========================================================
     */

    polygonStyle(style) {
        const option = {
            color: "#3388ff",

            weight: 2,

            opacity: 1,

            fillColor: "#3388ff",

            fillOpacity: 0.25,
        };

        if (style?.line) {
            option.color = style.line.color.color;

            option.weight = style.line.width;

            option.opacity = style.line.color.opacity;
        }

        if (style?.polygon) {
            option.fillColor = style.polygon.color.color;

            option.fillOpacity = style.polygon.color.opacity;
        }

        return option;
    }

    /**
     * =========================================================
     * Bounds
     * =========================================================
     */

    getBounds(layer) {
        if (!layer) return null;

        if (layer.getBounds) {
            return layer.getBounds();
        }

        if (layer.getLatLng) {
            return L.latLngBounds(
                layer.getLatLng(),

                layer.getLatLng(),
            );
        }

        return null;
    }

    /**
     * =========================================================
     * Center
     * =========================================================
     */

    getCenter(layer) {
        if (!layer) return null;

        if (layer.getBounds) {
            return layer.getBounds().getCenter();
        }

        if (layer.getLatLng) {
            return layer.getLatLng();
        }

        return null;
    }

    /**
     * =========================================================
     * Original Style
     * =========================================================
     */

    saveOriginalStyle(layer) {
        if (!layer.setStyle) return;
        if (layer._originalStyle) return;
        layer._originalStyle = {
            color: layer.options.color,

            weight: layer.options.weight,

            opacity: layer.options.opacity,

            fillColor: layer.options.fillColor,

            fillOpacity: layer.options.fillOpacity,
        };
    }

    restoreOriginalStyle(layer) {
        if (!layer.setStyle) return;

        if (!layer._originalStyle) return;

        layer.setStyle(layer._originalStyle);
    }
}

export default new Geometry();
